// vim: set ts=2 sts=2 sw=2 et:

import type { Lift } from "../../types/dataTypes";
import type { WrpfRecordCategory } from "../handoff/types";
import type { JudgeVoteValue } from "../judging/resolveJudgeVotes";
import { highestRecordCategory } from "../records/recordCategories";
import type { PlatformLiveAttempt, PlatformLiveAttemptSeriesItem } from "./platformLiveProtocol";

const LIFT_ORDER: ReadonlyArray<Lift> = ["S", "B", "D"];

// Solo los tres primeros intentos cuentan para el total; el 4º existe para récords.
const COUNTED_ATTEMPTS = 3;

export type LiftProgressState = "notStarted" | "inProgress" | "done" | "bombed";

export interface LiftProgressRow {
  lift: Lift;
  attempts: Array<PlatformLiveAttemptSeriesItem>;
  /** Mejor válido entre los tres primeros intentos; null si todavía no hay ninguno. */
  bestKg: number | null;
  state: LiftProgressState;
  current: boolean;
  /** Récord más alto logrado en este movimiento (incluye el 4º intento). */
  recordCategory?: WrpfRecordCategory;
}

export interface LiftProgress {
  rows: Array<LiftProgressRow>;
  /** Suma de los mejores válidos; null sin válidos o con un movimiento nulo. */
  totalKg: number | null;
  bombed: boolean;
  /** Con un solo movimiento no hay nada que acumular. */
  showTotal: boolean;
}

export interface LiftProgressReveal {
  attemptKey: string | null;
  result: JudgeVoteValue | null;
}

// Durante el reveal las pantallas muestran el intento tal como estaba ANTES del veredicto; se lo aplica acá
// para que el mejor y el total ya reflejen el resultado que el público está viendo.
const applyReveal = (
  attempt: PlatformLiveAttempt,
  lift: Lift,
  attempts: Array<PlatformLiveAttemptSeriesItem>,
  reveal: LiftProgressReveal | null | undefined,
): Array<PlatformLiveAttemptSeriesItem> => {
  const result = reveal?.result;
  if (reveal?.attemptKey !== attempt.key || lift !== attempt.lift || (result !== "good" && result !== "noLift")) {
    return attempts;
  }
  const index = attempt.attempt - 1;
  const next = [...attempts];
  next[index] = { ...(next[index] ?? { weightKg: attempt.weightKg }), status: result };
  return next;
};

const getState = (counted: Array<PlatformLiveAttemptSeriesItem>, current: boolean): LiftProgressState => {
  const resolved = counted.filter((item) => item.status !== "pending");
  if (resolved.length === COUNTED_ATTEMPTS && resolved.every((item) => item.status === "noLift")) return "bombed";
  if (resolved.length === COUNTED_ATTEMPTS) return "done";
  if (resolved.length === 0 && !current) return "notStarted";
  return "inProgress";
};

const makeRow = (
  attempt: PlatformLiveAttempt,
  lift: Lift,
  rawAttempts: Array<PlatformLiveAttemptSeriesItem>,
  reveal: LiftProgressReveal | null | undefined,
): LiftProgressRow => {
  const attempts = applyReveal(attempt, lift, rawAttempts, reveal);
  const counted = attempts.slice(0, COUNTED_ATTEMPTS);
  const goodWeights = counted.filter((item) => item.status === "good").map((item) => item.weightKg);
  const recordCategory = highestRecordCategory(
    attempts
      .filter((item) => item.status === "good" && item.recordAttempt === true && item.recordCategory !== undefined)
      .map((item) => item.recordCategory as WrpfRecordCategory),
  );
  const current = lift === attempt.lift;
  return {
    lift,
    attempts,
    bestKg: goodWeights.length > 0 ? Math.max(...goodWeights) : null,
    state: getState(counted, current),
    current,
    ...(recordCategory === undefined ? {} : { recordCategory }),
  };
};

export const getLiftProgress = (attempt: PlatformLiveAttempt, reveal?: LiftProgressReveal | null): LiftProgress => {
  const series = attempt.liftSeries ?? [{ lift: attempt.lift, attempts: attempt.attemptSeries ?? [] }];
  const rows = LIFT_ORDER.flatMap((lift) => {
    const liftSeries = series.find((candidate) => candidate.lift === lift);
    return liftSeries === undefined ? [] : [makeRow(attempt, lift, liftSeries.attempts, reveal)];
  });

  const bombed = rows.some((row) => row.state === "bombed");
  const bests = rows.flatMap((row) => (row.bestKg === null ? [] : [row.bestKg]));
  return {
    rows,
    totalKg: bombed || bests.length === 0 ? null : bests.reduce((sum, value) => sum + value, 0),
    bombed,
    showTotal: attempt.liftSeries !== undefined && rows.length > 1,
  };
};
